import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const SHELL_FADE = { initial: { opacity: 0, y: 16 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.5 } };

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function googleLogin() {
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

function AuthShell({ children, title, subtitle }) {
  return (
    <div className="min-h-screen bg-[#0A0A0B] flex fv-grain relative">
      <div className="hidden lg:flex flex-col justify-between w-1/2 p-14 relative overflow-hidden border-r border-white/8">
        <div className="relative z-10 flex items-center gap-2.5">
          <div className="w-10 h-10 rounded-lg fv-gold-gradient flex items-center justify-center">
            <span className="font-heading text-[#0A0A0B] text-2xl font-bold">F</span>
          </div>
          <div><div className="font-heading text-xl text-white">Fairview</div>
          <div className="text-[9px] tracking-[0.25em] uppercase text-[#D4AF37]">Systems</div></div>
        </div>
        <div className="relative z-10">
          <h2 className="font-heading text-4xl text-white leading-tight mb-4">The business operating<br />system for founders.</h2>
          <p className="text-[#A1A1AA] max-w-md">Form your entity, build credit, secure funding, and grow — all from one secure, luxurious command center.</p>
        </div>
        <div className="relative z-10 text-[#52525B] text-sm">© 2026 Fairview Systems</div>
        <div className="absolute -bottom-32 -right-32 w-96 h-96 rounded-full bg-[#D4AF37]/10 blur-3xl" />
      </div>
      <div className="flex-1 flex items-center justify-center p-6">
        <motion.div {...SHELL_FADE} className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-2.5 mb-8">
            <div className="w-10 h-10 rounded-lg fv-gold-gradient flex items-center justify-center">
              <span className="font-heading text-[#0A0A0B] text-2xl font-bold">F</span>
            </div>
            <div className="font-heading text-xl text-white">Fairview Systems</div>
          </div>
          <h1 className="font-heading text-3xl text-white mb-1.5">{title}</h1>
          <p className="text-[#A1A1AA] mb-8">{subtitle}</p>
          {children}
        </motion.div>
      </div>
    </div>
  );
}

export default function Login() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [tempToken, setTempToken] = useState("");
  const [code, setCode] = useState("");
  const [unverified, setUnverified] = useState(false);
  const [resendMsg, setResendMsg] = useState("");
  const [devUrl, setDevUrl] = useState("");
  const [resending, setResending] = useState(false);
  const [pendingDeletion, setPendingDeletion] = useState(false);
  const [recoveryDays, setRecoveryDays] = useState(0);
  const [restoring, setRestoring] = useState(false);

  useEffect(() => {
    const restoreEmail = new URLSearchParams(window.location.search).get("restore");
    if (restoreEmail) {
      setEmail(restoreEmail);
      setPendingDeletion(true);
      setError("This account is scheduled for deletion. Enter your password and restore it below.");
    }
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setUnverified(false); setResendMsg(""); setDevUrl(""); setPendingDeletion(false); setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.requires_2fa) {
        setTempToken(data.temp_token);
      } else {
        setUser(data);
        navigate("/dashboard");
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 403 && detail?.code === "email_unverified") {
        setUnverified(true);
        setError(detail.message);
      } else if (err.response?.status === 403 && detail?.code === "account_pending_deletion") {
        setPendingDeletion(true);
        setRecoveryDays(detail.recovery_days_remaining || 0);
        setError(detail.message);
      } else {
        setError(formatApiError(detail) || err.message);
      }
    } finally { setLoading(false); }
  };

  const restoreAccount = async () => {
    if (!email || !password) { setError("Enter your email and password to restore."); return; }
    setRestoring(true); setError("");
    try {
      await api.post("/auth/restore-account", { email, password });
      toast.success("Account restored! Signing you in…");
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data); navigate("/dashboard");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Could not restore account.");
    } finally { setRestoring(false); }
  };

  const resendVerification = async () => {
    if (!email) { setResendMsg("Enter your email above first."); return; }
    setResending(true); setResendMsg("");
    try {
      const { data } = await api.post("/auth/resend-verification", { email });
      setResendMsg(data?.message || "Verification email sent.");
      if (data?.dev_verification_url) setDevUrl(data.dev_verification_url);
      if (data?.email_status === "failed") setResendMsg(data?.email_error || "Email delivery failed. Please try again shortly.");
    } catch (err) {
      setResendMsg(formatApiError(err.response?.data?.detail) || "Couldn't resend right now. Please try again.");
    } finally { setResending(false); }
  };

  const submit2fa = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const { data } = await api.post("/auth/2fa/login", { temp_token: tempToken, code });
      setUser(data);
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  if (tempToken) {
    return (
      <AuthShell title="Two-factor authentication" subtitle="Enter the 6-digit code from your authenticator app.">
        <form onSubmit={submit2fa} className="space-y-4" data-testid="twofa-form">
          <div>
            <Label className="text-[#D4D4D8] mb-1.5 block">Authentication code</Label>
            <Input inputMode="numeric" autoFocus required value={code} onChange={(e) => setCode(e.target.value)}
              data-testid="twofa-code-input" placeholder="123456" maxLength={6}
              className="bg-[#121214] border-white/10 text-white h-11 tracking-[0.5em] text-center text-lg" />
          </div>
          {error && <p className="text-sm text-red-400" data-testid="twofa-error">{error}</p>}
          <Button type="submit" disabled={loading} data-testid="twofa-submit-btn"
            className="w-full fv-gold-gradient text-[#0A0A0B] font-semibold h-11 rounded-lg hover:opacity-90">
            {loading ? "Verifying…" : "Verify & sign in"}
          </Button>
        </form>
        <button onClick={() => { setTempToken(""); setCode(""); setError(""); }} data-testid="twofa-back-btn"
          className="text-center w-full text-sm text-[#A1A1AA] mt-6 hover:text-white">Use a different account</button>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Welcome back" subtitle="Sign in to your command center.">
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <div>
          <Label className="text-[#D4D4D8] mb-1.5 block">Email</Label>
          <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            data-testid="login-email-input" placeholder="you@company.com"
            className="bg-[#121214] border-white/10 text-white h-11" />
        </div>
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <Label className="text-[#D4D4D8]">Password</Label>
            <Link to="/forgot-password" className="text-xs text-[#D4AF37] hover:underline" data-testid="login-forgot-link">Forgot password?</Link>
          </div>
          <Input type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
            data-testid="login-password-input" placeholder="••••••••"
            className="bg-[#121214] border-white/10 text-white h-11" />
        </div>
        {error && <p className="text-sm text-red-400" data-testid="login-error">{error}</p>}
        {unverified && (
          <div className="rounded-xl border border-amber-500/25 bg-amber-500/5 p-4 space-y-3" data-testid="login-unverified-box">
            <p className="text-xs text-amber-400">Your email isn't verified yet. Resend the verification link below.</p>
            <Button type="button" onClick={resendVerification} disabled={resending} data-testid="login-resend-verify-btn"
              className="w-full h-10 rounded-lg bg-[#D4AF37] text-[#0A0A0B] font-semibold hover:opacity-90 disabled:opacity-60">
              {resending ? "Sending…" : "Resend verification email"}
            </Button>
            {resendMsg && <p className="text-xs text-[#D4D4D8]" data-testid="login-resend-msg">{resendMsg}</p>}
            {devUrl && (
              <div className="rounded-lg border border-amber-500/25 bg-black/30 p-3" data-testid="login-dev-verify-link">
                <p className="text-[11px] text-amber-400 mb-1">Email delivery isn't configured (log-only mode). Verify with this link:</p>
                <a href={devUrl} data-testid="login-dev-verify-anchor" className="text-xs text-[#D4AF37] hover:underline break-all">{devUrl}</a>
              </div>
            )}
          </div>
        )}
        {pendingDeletion && (
          <div className="rounded-xl border border-red-500/25 bg-red-500/5 p-4 space-y-3" data-testid="login-pending-deletion-box">
            <p className="text-xs text-red-300">
              This account is scheduled for deletion{recoveryDays ? ` and will be permanently removed in ${recoveryDays} day${recoveryDays === 1 ? "" : "s"}` : ""}. Restore it now to keep your data.
            </p>
            <Button type="button" onClick={restoreAccount} disabled={restoring} data-testid="login-restore-account-btn"
              className="w-full h-10 rounded-lg fv-gold-gradient text-[#0A0A0B] font-semibold hover:opacity-90 disabled:opacity-60">
              {restoring ? "Restoring…" : "Restore my account"}
            </Button>
          </div>
        )}
        <Button type="submit" disabled={loading} data-testid="login-submit-btn"
          className="w-full fv-gold-gradient text-[#0A0A0B] font-semibold h-11 rounded-lg hover:opacity-90">
          {loading ? "Signing in…" : "Sign in"}
        </Button>
      </form>
      <div className="flex items-center gap-3 my-5">
        <div className="flex-1 h-px bg-white/8" /><span className="text-[#71717A] text-xs">OR</span><div className="flex-1 h-px bg-white/8" />
      </div>
      <Button onClick={googleLogin} variant="outline" data-testid="google-login-btn"
        className="w-full bg-white text-[#0A0A0B] hover:bg-white/90 h-11 rounded-lg font-medium border-0">
        <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z"/><path fill="#EA4335" d="M12 4.75c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 1.48 14.97.5 12 .5A11 11 0 0 0 2.18 7.06l3.66 2.84C6.71 6.68 9.14 4.75 12 4.75z"/></svg>
        Continue with Google
      </Button>
      <p className="text-center text-sm text-[#A1A1AA] mt-6">
        New to Fairview? <Link to="/register" className="text-[#D4AF37] hover:underline" data-testid="goto-register-link">Create an account</Link>
      </p>
    </AuthShell>
  );
}

export { AuthShell, googleLogin };
